import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Icon, SidelistOption } from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import SearchInput from "@/components/common/search-input";
import {
  NodeTypes,
  type NodeData,
  type RefName,
} from "@/workflow_api/types/workflow";
import { useUIStore } from "../store/uiStore";
import { useWorkflowStore } from "../store/workflowStore";
import { useCreateNode, useUpdateNode } from "../services/nodes.api";
import { useParams } from "react-router";
import { cn } from "@/utils/theme";

type NodeOption = {
  type: string;
  title: string;
  subtitle: string;
  color: "action" | "success" | "warning" | "info" | "neutral" | "danger";
  icon: { type: string; name: string };
  children?: NodeOption[];
};

const nodeOptions: NodeOption[] = [
  {
    type: "form",
    title: "Opciones de formulario",
    color: "action",
    subtitle: "Agrega funciones de formulario",
    icon: { type: "fas", name: "bars" },
    children: [
      {
        type: NodeTypes.FORM,
        title: "Formulario",
        color: "action",
        subtitle: "Crea un formulario personalizado",
        icon: { type: "fas", name: "bars" },
      },
      {
        type: NodeTypes.CORRECTION,
        title: "Revisión",
        color: "action",
        subtitle: "Revisa y corrige formularios del flujo",
        icon: { type: "fas", name: "file-edit" },
      },
    ],
  },
  {
    type: "opeartor",
    title: "Operadores",
    color: "neutral",
    subtitle: "Crea decisiones dentro del proceso de tu flujo",
    icon: { type: "fas", name: "toggle-on" },
    children: [
      {
        type: NodeTypes.IF,
        title: "Condicional",
        color: "neutral",
        subtitle: "Crea una condición para tu flujo",
        icon: { type: "fas", name: "toggle-on" },
      },
      {
        type: NodeTypes.SWITCH,
        title: "Switch",
        color: "neutral",
        subtitle: "Crea un switch para tu flujo",
        icon: { type: "fas", name: "arrows-cross" },
      },
      {
        type: NodeTypes.MULTITHREAD,
        title: "Multihilos",
        color: "neutral",
        subtitle: "Crea salidas paralelas para procesos concurrentes",
        icon: { type: "fas", name: "code-branch" },
      },
      {
        type: NodeTypes.WAIT,
        title: "Wait",
        color: "neutral",
        subtitle: "Espera respuestas de hilos o subflujos antes de continuar",
        icon: { type: "fas", name: "hourglass-half" },
      },
      {
        type: NodeTypes.MERGE,
        title: "Merge",
        color: "neutral",
        subtitle: "Combina múltiples flujos de datos en uno solo",
        icon: { type: "fas", name: "code-merge" },
      },
    ],
  },
  {
    type: "action",
    title: "Acciones",
    color: "info",
    subtitle: "Define acciones automáticas",
    icon: { type: "fas", name: "grid-2-plus" },
    children: [
      {
        type: NodeTypes.ASSIGN_GROUP,
        title: "Asignar grupo",
        color: "info",
        subtitle: "Asigna un grupo a un usuario",
        icon: { type: "fas", name: "users" },
      },
      {
        type: NodeTypes.DATA_CENTER,
        title: "Centro de Datos",
        color: "info",
        subtitle: "Realiza operaciones con la información del centro de datos",
        icon: { type: "fas", name: "table" },
      },
      {
        type: NodeTypes.EXPEDIENTE,
        title: "Expediente",
        color: "info",
        subtitle: "Gestiona expedientes digitales de la organización",
        icon: { type: "fas", name: "folder-open" },
      },
      {
        type: NodeTypes.NOTIFICATION,
        title: "Notificación (Email/SMS)",
        color: "info",
        subtitle: "Envía notificaciones automáticas por email o SMS",
        icon: { type: "fas", name: "bells" },
      },
    ],
  },
  {
    type: "data-transform",
    title: "Transformación de datos",
    color: "success",
    subtitle: "Selecciona la data ingresada en tu centro de datos",
    icon: { type: "fas", name: "right-left-large" },
    children: [
      {
        type: NodeTypes.CALCULATED,
        title: "Campo calculado",
        color: "success",
        subtitle: "Crea un campo calculado en tu flujo",
        icon: { type: "fas", name: "function" },
      },
    ],
  },
  {
    type: "integration",
    title: "Integraciones",
    color: "warning",
    subtitle: "Conecta con servicios externos",
    icon: { type: "fas", name: "object-intersect" },
    children: [
      {
        type: NodeTypes.PAYMENT,
        title: "Pasarela de pago",
        color: "warning",
        subtitle: "Procesa pagos con servicios externos",
        icon: { type: "fas", name: "dollar-sign" },
      },
      {
        type: NodeTypes.N8N,
        title: "Objeto N8N",
        color: "warning",
        subtitle: "Conecta un flujo de N8N",
        icon: { type: "fas", name: "hexagon-nodes" },
      },
      {
        type: NodeTypes.INTEROPERABILITY,
        title: "Interoperabilidad",
        color: "warning",
        subtitle: "Crea conexiones entre instituciones",
        icon: { type: "fas", name: "diagram-nested" },
      },
    ],
  },
  {
    type: "doc-generator",
    title: "Opciones de documentos",
    color: "action",
    subtitle: "Agrega funciones de documentos",
    icon: { type: "fas", name: "file-lines" },
    children: [
      {
        type: NodeTypes.DOC_GENERATOR,
        title: "Generador de documentos",
        color: "action",
        subtitle: "Crea y edita documentos en tu flujo",
        icon: { type: "fas", name: "file-lines" },
      },
      {
        type: NodeTypes.SIGN_DOCUMENT,
        title: "Firma digital",
        color: "action",
        subtitle: "Firma de documentos",
        icon: { type: "fas", name: "signature" },
      },
      {
        type: NodeTypes.RESOLUTION,
        title: "Resolución de trámites",
        color: "action",
        subtitle: "Reune todos los archivos de resolución",
        icon: { type: "fas", name: "files" },
      },
    ],
  },
  {
    type: NodeTypes.END,
    title: "Objeto de finalización",
    color: "neutral",
    icon: {
      type: "fas",
      name: "stop",
    },
    subtitle: "Finaliza el flujo de trabajo",
  },
];

const NodeSelectionModal = () => {
  const params = useParams();
  const { mutateAsync: createNode, isPending } = useCreateNode(params.flowId!);
  const { mutateAsync: updateNode } = useUpdateNode(params.flowId!);

  const {
    modalState,
    connectionToSplit,
    setNodeCreatorOpen,
    setConnectionToSplit,
    clearErrors,
  } = useUIStore();
  const {
    createNodeAndEdge,
    lastClickPosition,
    getNodeById,
    createOrphanNode,
    removeWaitNodeConnection,
  } = useWorkflowStore();
  // const {
  //   addInitialNode,
  //   addNodeAndSplitConnection,
  //   addNodeAndConnect,
  //   addOrphanNode,
  //   nodes,
  //   lastClickPosition,
  // } = useWorkflowStore();

  const handleAddNode = async (nodeType: RefName) => {
    if (isPending) return;
    if (!Object.values(NodeTypes).includes(nodeType)) {
      setNodeCreatorOpen(false);
      setSearchQuery("");
      setSelectedCategory(null);
      return;
    }

    clearErrors();

    let position = {
      x: Math.floor(lastClickPosition.x + (Math.random() - 0.1) * 100),
      y: lastClickPosition.y,
    };

    if (connectionToSplit) {
      if (connectionToSplit.edgeId) {
        // Calculate position in the middle of the edge between source and target nodes
        const sourceNode = getNodeById(connectionToSplit.sourceNodeId);
        const targetNode = getNodeById(connectionToSplit.targetNodeId);

        if (sourceNode && targetNode) {
          // Calculate midpoint between source and target nodes
          position = {
            x: Math.floor((sourceNode.position.x + targetNode.position.x) / 2),
            y: Math.floor((sourceNode.position.y + targetNode.position.y) / 2),
          };
        }

        // insert node in the middle of the edge
        const newNode = createNodeAndEdge({
          type: nodeType,
          position,
          source: {
            source: connectionToSplit.sourceNodeId,
            source_handler: connectionToSplit.sourceHandle,
          },
        });
        if (!targetNode) return;
        if (!newNode) return;
        const targetData = targetNode.data as unknown as NodeData;

        // Extract edge and node data for use in both Wait and non-Wait node handling
        const { edge: newEdge, ...newNodeWithoutEdge } = newNode;

        // Special handling for Wait nodes - they use inputs with source_connections instead of edge
        if (
          targetData.type === NodeTypes.WAIT &&
          Array.isArray(targetData.inputs) &&
          connectionToSplit.targetHandle
        ) {
          // Step 1: Create the new node connected to the original source node
          const createdNodeResponse = await createNode({
            node: {
              ...newNodeWithoutEdge,
              flow_id: params.flowId!,
              ...(newEdge !== null && newEdge !== undefined
                ? { edge: newEdge }
                : {}),
            },
          });

          // Step 2: Remove the original connection from the Wait node's input
          const updatedInputs = removeWaitNodeConnection(
            connectionToSplit.targetNodeId,
            connectionToSplit.targetHandle,
            connectionToSplit.sourceNodeId,
            connectionToSplit.sourceHandle,
          );

          if (!updatedInputs) return;

          // Find the target input index to add the new connection
          const handleMatch =
            connectionToSplit.targetHandle.match(/^input-(\d+)$/);
          if (!handleMatch) return;

          const inputIndex = parseInt(handleMatch[1], 10) - 1;
          const threadInputs = updatedInputs.filter(
            (input) => input.type === "THREAD",
          );

          if (inputIndex < 0 || inputIndex >= threadInputs.length) return;

          // Find the actual input array index
          let threadCount = 0;
          let targetInputArrayIndex = -1;
          for (let i = 0; i < updatedInputs.length; i++) {
            if (updatedInputs[i].type === "THREAD") {
              if (threadCount === inputIndex) {
                targetInputArrayIndex = i;
                break;
              }
              threadCount++;
            }
          }

          if (targetInputArrayIndex === -1) return;

          // Determine the source handler for the new node
          const newSourceHandler = (() => {
            if (newNode.ref_name === NodeTypes.IF) {
              return "true";
            }
            if (newNode.ref_name === NodeTypes.SWITCH) {
              return "connection_0";
            }
            if (newNode.ref_name === NodeTypes.MULTITHREAD) {
              return "thread_1";
            }
            if (newNode.ref_name === NodeTypes.WAIT) {
              return "success";
            }

            return "source";
          })();

          // Step 3: Add the new node connection to the Wait node's input
          // Get the new node ID from the response
          // The mutationFn returns response.data, so createdNodeResponse should be the node object directly
          const newNodeId =
            createdNodeResponse?.id || createdNodeResponse?.data?.id;
          if (!newNodeId) {
            console.error(
              "Failed to get new node ID from creation response",
              createdNodeResponse,
            );
            return;
          }

          const targetInput = updatedInputs[targetInputArrayIndex];
          const existingConnections = targetInput.source_connections || [];

          // Check if connection already exists
          const alreadyConnected = existingConnections.some(
            (conn) =>
              conn?.source_node_id === newNodeId &&
              (conn?.field?.id || conn?.source_node_output_id) ===
                newSourceHandler,
          );

          if (!alreadyConnected) {
            existingConnections.push({
              field: {
                id: newSourceHandler,
                name: newSourceHandler,
                type: "thread",
              },
              source_node_id: newNodeId,
            });
          }

          updatedInputs[targetInputArrayIndex] = {
            ...targetInput,
            source_connections: existingConnections,
            connection: targetInput.connection || newNodeId,
          };

          // Build edges array for API
          const existingEdgesMap = new Map<string, string>();
          if (targetData.edges && Array.isArray(targetData.edges)) {
            targetData.edges.forEach((edge) => {
              if (edge.target_handler && edge.source_handler && edge.source) {
                existingEdgesMap.set(
                  `${edge.source}-${edge.target_handler}`,
                  edge.source_handler,
                );
              }
            });
          }

          const threadInputsOnly = updatedInputs.filter(
            (input) => input.type === "THREAD",
          );
          const apiEdges = threadInputsOnly
            .flatMap((input, idx) => {
              if (!Array.isArray(input.source_connections)) return [];
              const targetHandler = `input-${idx + 1}`;
              return input.source_connections
                .map((connection) => {
                  if (!connection.source_node_id) return null;
                  let sourceHandler =
                    connection.field?.id || connection.source_node_output_id;
                  if (!sourceHandler) {
                    sourceHandler =
                      existingEdgesMap.get(
                        `${connection.source_node_id}-${targetHandler}`,
                      ) || "source";
                  }
                  return {
                    source: connection.source_node_id,
                    source_handler: sourceHandler,
                    target_handler: targetHandler,
                    target_input_name: input.name,
                  };
                })
                .filter(
                  (edge): edge is NonNullable<typeof edge> => edge !== null,
                );
            })
            .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

          // Step 4: Update the Wait node with the new connection
          await updateNode({
            node_id: connectionToSplit.targetNodeId,
            node: {
              display_name: targetData.displayName,
              flow_id: params.flowId!,
              order: targetData.order,
              position_x: targetNode.position.x,
              position_y: targetNode.position.y,
              ref_name: targetData.type as RefName,
              type: targetData.node_type,
              edges: apiEdges.length > 0 ? apiEdges : undefined,
              inputs: updatedInputs,
            },
          });
        } else {
          // Default handling for non-Wait nodes
          updateNode({
            node_id: connectionToSplit.targetNodeId,
            node: {
              display_name: targetData.displayName,
              flow_id: params.flowId!,
              order: targetData.order + 1,
              position_x: targetNode.position.x,
              position_y: targetNode.position.y,
              ref_name: targetData.type as RefName,
              type: targetData.node_type,
              edge: {
                source_handler: (() => {
                  if (newNode.ref_name === NodeTypes.IF) {
                    return "true";
                  }
                  if (newNode.ref_name === NodeTypes.SWITCH) {
                    return "connection_0";
                  }
                  if (newNode.ref_name === NodeTypes.MULTITHREAD) {
                    return "thread_1";
                  }
                  if (
                    newNode.ref_name === NodeTypes.CORRECTION ||
                    newNode.ref_name === NodeTypes.WAIT ||
                    newNode.ref_name === NodeTypes.DATA_CENTER ||
                    newNode.ref_name === NodeTypes.PAYMENT ||
                    newNode.ref_name === NodeTypes.N8N ||
                    newNode.ref_name === NodeTypes.INTEROPERABILITY ||
                    newNode.ref_name === NodeTypes.DOC_GENERATOR ||
                    newNode.ref_name === NodeTypes.SIGN_DOCUMENT
                  ) {
                    return "success";
                  }
                  return "source";
                })(),
                target_handler: "target",
              },
              new_node: {
                ...newNodeWithoutEdge,
                flow_id: params.flowId!,
                ...(newEdge !== null && newEdge !== undefined
                  ? { edge: newEdge }
                  : {}),
              },
            },
          });
        }
      } else {
        // add node with edge
        const node = createNodeAndEdge({
          type: nodeType,
          position,
          source: {
            source: connectionToSplit.sourceNodeId,
            source_handler: connectionToSplit.sourceHandle,
          },
        });
        if (!node) return;
        const { edge, ...nodeWithoutEdge } = node;
        await createNode({
          node: {
            ...nodeWithoutEdge,
            flow_id: params.flowId!,
            ...(edge !== null && edge !== undefined ? { edge } : {}),
          },
        });
      }
      setConnectionToSplit(undefined); // Clear connection after adding node
      setNodeCreatorOpen(false);
      setSearchQuery("");
      setSelectedCategory(null);
      return;
    } else {
      // add orphan node
      const newNode = createOrphanNode({
        type: nodeType,
        position,
      });
      if (!newNode) return;
      const { edge, ...newNodeWithoutEdge } = newNode;
      await createNode({
        node: {
          ...newNodeWithoutEdge,
          flow_id: params.flowId!,
          ...(edge !== null && edge !== undefined ? { edge } : {}),
        },
      });
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<NodeOption[] | null>(
    null,
  );

  const filteredNodeOptions = useMemo(() => {
    if (!searchQuery) return nodeOptions;
    //flat node options, put children in the same level as parents, and delete parent as it is not needed
    const flatOptions: NodeOption[] = [];
    const flattenOptions = (options: NodeOption[]) => {
      options.forEach((option) => {
        if (option.children) {
          flattenOptions(option.children);
        } else {
          flatOptions.push(option);
        }
      });
    };
    flattenOptions(nodeOptions);
    return flatOptions.filter(
      (option) =>
        option.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.subtitle.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery]);

  const clearStateTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (clearStateTimeoutRef.current !== null) {
        clearTimeout(clearStateTimeoutRef.current);
        clearStateTimeoutRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    setNodeCreatorOpen(false);
    if (clearStateTimeoutRef.current !== null) {
      clearTimeout(clearStateTimeoutRef.current);
      clearStateTimeoutRef.current = null;
    }
    clearStateTimeoutRef.current = window.setTimeout(() => {
      setSearchQuery("");
      setSelectedCategory(null);
      setConnectionToSplit(undefined); // Clear connection when closing modal
    }, 100); // Delay to allow modal to close before clearing state
  };

  return (
    <Dialog open={modalState === "create"} onOpenChange={handleClose}>
      <DialogContent
        showCloseButton={false}
        type="side"
        overlay={false}
        className="max-w-110 max-h-[calc(100vh-78px)] mt-17.25 mr-3 bg-neutral-object border-l border-neutral-quiet rounded-none p-0"
      >
        <div className="w-full flex flex-col gap-1.5 p-4 border-b border-neutral-quiet">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-h6 font-semibold text-heading">
              Agrega un elemento a tu flujo
            </DialogTitle>
            <Button
              size="icon"
              variant="quiet"
              aria-label="Cerrar selección de nodo"
              type="button"
              onClick={handleClose}
            >
              <Icon type="far" name="times" />
            </Button>
          </DialogHeader>
          <p className="text-sm leading-2xl text-body font-normal">
            Los nodos son los elementos principales que crean una acción dentro
            de tu flujo, selecciona todo lo que necesites.
          </p>
          <div className="flex w-full gap-1 pt-3.5">
            {selectedCategory && (
              <Button
                size="icon"
                variant="quiet"
                aria-label="regresar"
                type="button"
                onClick={() => {
                  setSelectedCategory(null);
                }}
              >
                <Icon type="far" name="angle-left" className="text-body-loud" />
              </Button>
            )}
            <div className="w-full">
              <SearchInput
                placeholder="Buscar..."
                value={searchQuery}
                variant="xs"
                className="w-full"
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col overflow-auto">
          {selectedCategory &&
            selectedCategory.map((option) => {
              return (
                <SidelistOption
                  key={option.type}
                  title={option.title}
                  subtitle={option.subtitle}
                  active={false}
                  color={option.color}
                  tabIndex={0}
                  className={cn(
                    "w-full bg-neutral-object flex items-stretch min-h-17 gap-4 pl-4 relative hover:bg-neutral-object-accent-silent hover:cursor-pointer transition-all duration-75 ease-out",
                    isPending && "pointer-events-none opacity-50",
                  )}
                  onClick={() => {
                    if (isPending) return;
                    if ((option.children?.length || 0) > 0) {
                      setSelectedCategory(option.children || []);
                      return; // Skip if there are children, as this is a parent node
                    }
                    handleAddNode(option.type as RefName);
                  }}
                  icon={option.icon}
                  style={{
                    cursor:
                      (option.children?.length || 0) > 0
                        ? "pointer"
                        : "default",
                  }}
                >
                  {(option.children?.length || 0) > 0 && (
                    <SidelistOption.Right>
                      <div className="size-8 flex items-center justify-center pr-4">
                        <Icon
                          type="far"
                          name="angle-right"
                          className="text-body-loud"
                        />
                      </div>
                    </SidelistOption.Right>
                  )}
                </SidelistOption>
              );
            })}
          {selectedCategory === null &&
            filteredNodeOptions.map((option) => {
              return (
                <SidelistOption
                  key={option.type}
                  title={option.title}
                  subtitle={option.subtitle}
                  active={false}
                  color={isPending ? "neutral" : option.color}
                  tabIndex={0}
                  className={cn(
                    "w-full bg-neutral-object flex items-stretch min-h-17 gap-4 pl-4 relative hover:bg-neutral-object-accent-silent hover:cursor-pointer transition-all duration-75 ease-out",
                    isPending && "pointer-events-none opacity-50",
                  )}
                  onClick={() => {
                    if (isPending) return;
                    if ((option.children?.length || 0) > 0) {
                      setSelectedCategory(option.children || []);
                      return; // Skip if there are children, as this is a parent node
                    }
                    handleAddNode(option.type as RefName);
                  }}
                  icon={option.icon}
                  style={{
                    cursor:
                      (option.children?.length || 0) > 0
                        ? "pointer"
                        : "default",
                  }}
                >
                  {(option.children?.length || 0) > 0 && (
                    <SidelistOption.Right>
                      <div className="size-8 flex items-center justify-center pr-4">
                        <Icon
                          type="far"
                          name="angle-right"
                          className="text-body-loud"
                        />
                      </div>
                    </SidelistOption.Right>
                  )}
                </SidelistOption>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NodeSelectionModal;
